import React from 'react';
import Link from 'next/link';
import Image from 'next/image';

import PropTypes from 'prop-types';

import {
  Popover,
  PopoverDescription,
  PopoverHeading,
  usePopoverState,
} from 'ariakit/popover';

import { motion, AnimatePresence } from 'framer-motion';

import ButtonSecondaryEllipse from '../ButtonSecondaryEllipse';
import { useTranslation } from 'next-i18next';

import s from './InfoPopover.module.scss';

const InfoPopover = ({ title, text, link, color, background }) => {
  const { t } = useTranslation();
  const popover = usePopoverState({
    placement: 'top-center',
    gutter: 16,
    unstable_flip: false,
    flip: false,
  });

  const externalLink = link?.href?.includes('http');

  const closeIcon = background === 'white-glass' ? 'icon-cross-black.svg' : 'icon-cross-white.svg';

  return (
    <>
      <AnimatePresence>
        {popover.open && (
          <motion.div
            className={s.PopoverAnimationWrapper}
            key="popover"
            initial={{ opacity: 0, marginTop: 20 }}
            animate={{
              opacity: 1,
              marginTop: 0,
              transition: {
                opacity: { duration: 0.08 },
                marginTop: { duration: 0.25 },
              },
            }}
            exit={{
              opacity: 0,
              marginTop: 20,
              transition: {
                opacity: { duration: 0.08 },
                marginTop: { duration: 0.25 },
              },
            }}>
            <Popover state={popover} className={`${s.Popover} ${background}`}>
              <button className={s.PopoverCloseButton} onClick={popover.hide}>
                <span className="sr-only">{t('Generic.close')}</span>
                <Image
                  className={s.PopoverCloseIcon}
                  width={16}
                  height={16}
                  src={`/img/${closeIcon}`}
                  alt=""
                />
              </button>
              <PopoverHeading className={s.PopoverHeading}>
                {title}
              </PopoverHeading>
              <PopoverDescription className={s.PopoverDescription}>
                {text}
              </PopoverDescription>
              {link?.href && (
                <div className={s.PopoverSource}>
                  {t('Generic.source')}:&nbsp;
                  <Link
                    className={s.PopoverLink}
                    href={link?.href}
                    target={externalLink ? '_blank' : '_self'}>
                    {link?.text}
                    {externalLink && (
                      <Image
                        className={s.PopoverLinkIcon}
                        width={10}
                        height={10}
                        src={`/img/icon-external-white.svg`}
                        alt=""
                      />
                    )}
                  </Link>
                </div>
              )}
            </Popover>
          </motion.div>
        )}
      </AnimatePresence>
      <ButtonSecondaryEllipse
        size="x-small"
        icon={`icon-info-${color}`}
        onClick={popover.toggle}
        modifiers={[color === 'black' ? 'Black' : '', 'NoPadding']}
        ref={popover.anchorRef}>
        <span className={`${s.Desktop} sr-only`}>{t('Generic.info')}</span>
        <span className={s.Responsive}>{t('Generic.info')}</span>
      </ButtonSecondaryEllipse>
    </>
  );
};

InfoPopover.propTypes = {
  title: PropTypes.string,
  text: PropTypes.string,
  color: PropTypes.string,
  link: PropTypes.object,
  background: PropTypes.string,
};

InfoPopover.defaultProps = {
  color: 'black',
  background: 'black-glass',
};

export default InfoPopover;
